import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import HubLayout from '../../components/HubLayout';
import { api } from '../../api/client';
import MetricCard from '../../components/MetricCard';

// Build version: 2026-01-15-v2 - Force cache bust
// Import insights dashboard for that tab
import InsightsDashboard from '../../components/InsightsDashboard';

const DashboardHub = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [inventoryData, setInventoryData] = useState([]);
  const [filteredAlerts, setFilteredAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [metrics, setMetrics] = useState([
    { icon: '💰', label: 'PROFIT', value: '$0', subtitle: '0%', status: 'default' },
    { icon: '📦', label: 'STOCK', value: '...', subtitle: 'loading', status: 'default' },
    { icon: '💵', label: 'INVENTORY', value: '$0', subtitle: '...', status: 'default' },
    { icon: '📊', label: 'SALES', value: '$0', subtitle: 'last 30 days', status: 'default' },
    { icon: '⚠️', label: 'ALERTS', value: '0', subtitle: 'need attention', status: 'default' },
    { icon: '👥', label: 'ORDERS', value: '0', subtitle: 'today', status: 'default' }
  ]);

  // Calculated KPIs
  const integratedKPIs = useMemo(() => {
    const activeItems = inventoryData.filter(item => {
      const currentStock = item.current_stock ?? item.inventory?.current_stock ?? item.planning?.on_hand ?? item.on_hand ?? 0;
      return currentStock > 0;
    });
    
    // Helper to get product cost from any available field
    const getProductCost = (item) => {
      // Check all possible cost field locations
      return (
        item.pricing_v2?.total_cost ||           // Most common for imported products
        item.pricing_v2?.manufacturing_cost?.total ||
        item.pricing_v2?.manufacturing_cost ||
        item.cost_price ||
        item.manufacturing_cost ||
        item.unit_cost ||
        item.wholesale_price ||
        item.effective_cost ||
        item.cogs ||
        item.purchase_price ||
        0
      );
    };
    
    const totalValue = inventoryData.reduce((sum, item) => {
      const currentStock = item.current_stock ?? item.inventory?.current_stock ?? item.planning?.on_hand ?? item.on_hand ?? 0;
      const unitCost = getProductCost(item);
      return sum + (currentStock * unitCost);
    }, 0);
    
    const itemsWithPricing = inventoryData.filter(item => {
      const hasPrice = item.retail_price && item.retail_price > 0;
      const hasCost = getProductCost(item);
      return hasPrice || (hasCost && hasCost > 0);
    });
    const pricingCompleteness = inventoryData.length > 0 ? (itemsWithPricing.length / inventoryData.length) * 100 : 0;

    const profitableItems = inventoryData.filter(item => {
      const price = item.retail_price ?? item.price ?? 0;
      const cost = getProductCost(item);
      return price > 0 && cost > 0 && price > cost;
    });
    const averageMargin = profitableItems.length > 0
      ? profitableItems.reduce((sum, item) => {
          const price = item.retail_price ?? item.price ?? 0;
          const cost = getProductCost(item);
          return sum + (((price - cost) / price) * 100);
        }, 0) / profitableItems.length
      : 0;

    const lowStockCount = inventoryData.filter(item => {
      const currentStock = item.current_stock ?? item.inventory?.current_stock ?? item.planning?.on_hand ?? item.on_hand ?? 0;
      const reorderPoint = item.reorder_point ?? item.inventory?.reorder_point ?? item.planning?.reorder_point ?? 10;
      return currentStock > 0 && currentStock < reorderPoint;
    }).length;
    
    const outOfStockCount = inventoryData.filter(item => {
      const currentStock = item.current_stock ?? item.inventory?.current_stock ?? item.planning?.on_hand ?? item.on_hand ?? 0;
      return currentStock === 0;
    }).length;

    return {
      totalValue,
      activeItems: activeItems.length,
      totalItems: inventoryData.length,
      pricingCompleteness,
      averageMargin,
      lowStockCount,
      outOfStockCount,
      stockHealth: inventoryData.length > 0 ? ((inventoryData.length - lowStockCount - outOfStockCount) / inventoryData.length) * 100 : 0
    };
  }, [inventoryData]);

  // AI Recommendations
  const aiRecommendations = useMemo(() => {
    const recommendations = [];
    if (integratedKPIs.averageMargin < 30 && integratedKPIs.averageMargin > 0) {
      recommendations.push({ icon: '📈', title: 'Margin Optimization', suggestion: 'Increase prices by 5-8% to achieve target 30% margin', impact: 'Potential revenue increase: $2,500/month', confidence: 85 });
    }
    if (integratedKPIs.lowStockCount > 5) {
      recommendations.push({ icon: '📦', title: 'Inventory Rebalancing', suggestion: `Reorder ${integratedKPIs.lowStockCount} low-stock SKUs`, impact: 'Prevent potential lost sales', confidence: 92 });
    }
    if (integratedKPIs.pricingCompleteness < 90) {
      recommendations.push({ icon: '💡', title: 'Pricing Data Completion', suggestion: 'Complete missing price/cost data', impact: 'Enable full profit optimization', confidence: 100 });
    }
    return recommendations;
  }, [integratedKPIs]);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Build v2: Explicit URL logging for debug
        const productUrl = '/api/v1/product-master/list?tenant_id=tenant-123&page_size=500';
        console.log('🔍 DashboardHub: Fetching products from:', productUrl);
        
        // Fetch ALL product data (remove page_size limit for accurate KPIs)
        const productData = await api.get(productUrl);
        console.log('✅ DashboardHub: Products received:', productData?.items?.length || 0);
        const products = productData.items || [];
        setInventoryData(products);
        
        // Fetch alerts
        try {
          const alertsResponse = await api.get('/api/v1/alert-preferences/filtered');
          setFilteredAlerts(alertsResponse.alerts || []);
        } catch (e) {
          console.log('Alerts fetch failed, using empty');
        }
        
        // Fetch veeqo orders for sales metrics
        const ordersResponse = await api.get('/api/v1/veeqo/orders?page=1&page_size=100', { headers: { 'X-Tenant-ID': 'tenant-123' } });
        const orders = ordersResponse.orders || [];
        
        // Calculate metrics using correct fields
        const lowStockCount = products.filter(p => {
          const currentStock = p.current_stock ?? p.inventory?.current_stock ?? p.total_qty ?? 0;
          const reorderPoint = p.reorder_point ?? p.inventory?.reorder_point ?? 10;
          return currentStock > 0 && currentStock < reorderPoint;
        }).length;
        
        const totalInventoryValue = products.reduce((sum, p) => {
          const qty = p.current_stock ?? p.inventory?.current_stock ?? p.total_qty ?? 0;
          const cost = p.cost_price ?? p.pricing_v2?.manufacturing_cost?.total ?? p.wholesale_price ?? 0;
          return sum + (qty * cost);
        }, 0);
        
        const last30DaysOrders = orders.filter(o => {
          const orderDate = new Date(o.created_at);
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          return orderDate >= thirtyDaysAgo;
        });
        
        const totalSales = last30DaysOrders.reduce((sum, o) => sum + (o.total_price || 0), 0);
        const totalProfit = last30DaysOrders.reduce((sum, o) => sum + (o.gross_profit || 0), 0);
        const avgMargin = totalSales > 0 ? (totalProfit / totalSales * 100) : 0;
        
        const today = new Date().toDateString();
        const todayOrders = orders.filter(o => new Date(o.created_at).toDateString() === today).length;
        
        // Update top metrics
        setMetrics([
          { icon: '💰', label: 'PROFIT', value: `$${(totalProfit / 1000).toFixed(1)}K`, subtitle: `${avgMargin.toFixed(1)}%`, trend: totalProfit > 0 ? '↑ Good' : '→ Neutral', status: totalProfit > 40000 ? 'green' : totalProfit > 20000 ? 'yellow' : 'red', onClick: () => navigate('/sales') },
          { icon: '📦', label: 'STOCK', value: lowStockCount > 0 ? `⚠️ ${lowStockCount}` : '✓ Good', subtitle: `${products.length} SKUs`, status: lowStockCount === 0 ? 'green' : lowStockCount < 5 ? 'yellow' : 'red', onClick: () => navigate('/inventory') },
          { icon: '💵', label: 'INVENTORY', value: `$${(totalInventoryValue / 1000).toFixed(1)}K`, subtitle: 'total value', status: 'green', onClick: () => navigate('/inventory') },
          { icon: '📊', label: 'SALES', value: `$${(totalSales / 1000).toFixed(1)}K`, subtitle: 'last 30 days', status: totalSales > 100000 ? 'green' : 'yellow', onClick: () => navigate('/sales') },
          { icon: '⚠️', label: 'ALERTS', value: lowStockCount.toString(), subtitle: 'need attention', status: lowStockCount > 0 ? 'yellow' : 'green', onClick: () => setActiveTab('alerts') },
          { icon: '👥', label: 'ORDERS', value: todayOrders.toString(), subtitle: 'today', status: 'green', onClick: () => navigate('/sales') }
        ]);
        
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'insights', label: 'Insights', icon: '💡' },
    { id: 'alerts', label: 'Alerts', icon: '🚨' }
  ];

  const actions = (
    <button 
      onClick={() => window.location.reload()}
      className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-700 transition-colors"
    >
      🔄 Refresh
    </button>
  );

  return (
    <HubLayout
      icon="🏠"
      title="Dashboard Hub"
      subtitle="Overview of your business health and key metrics"
      metrics={metrics}
      tabs={tabs}
      defaultTab="overview"
      actions={actions}
    >
      {(activeTab) => (
        <>
          {activeTab === 'overview' && (
            <div className="space-y-3">
              {/* Quick Stats - COMPACT MetricCards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <MetricCard
                  icon="💰"
                  label="INVENTORY VALUE"
                  value={`$${integratedKPIs.totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  subtitle={`${integratedKPIs.activeItems} active SKUs`}
                  status="default"
                />
                <MetricCard
                  icon="📊"
                  label="AVG MARGIN"
                  value={`${integratedKPIs.averageMargin.toFixed(1)}%`}
                  subtitle="Across profitable items"
                  status={integratedKPIs.averageMargin > 30 ? 'green' : integratedKPIs.averageMargin > 15 ? 'yellow' : 'red'}
                />
                <MetricCard
                  icon="📦"
                  label="STOCK HEALTH"
                  value={`${integratedKPIs.stockHealth.toFixed(0)}%`}
                  subtitle={`${integratedKPIs.lowStockCount} low, ${integratedKPIs.outOfStockCount} out`}
                  status={integratedKPIs.stockHealth > 90 ? 'green' : integratedKPIs.stockHealth > 70 ? 'yellow' : 'red'}
                />
                <MetricCard
                  icon="💲"
                  label="PRICING"
                  value={`${integratedKPIs.pricingCompleteness.toFixed(0)}%`}
                  subtitle="Complete data"
                  status={integratedKPIs.pricingCompleteness > 90 ? 'green' : integratedKPIs.pricingCompleteness > 50 ? 'yellow' : 'red'}
                />
              </div>

              {/* Alerts & Recommendations - COMPACT */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {/* Active Alerts - COMPACT */}
                <div className="bg-slate-800 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xs font-semibold text-white flex items-center gap-1.5">
                      <span>🔔</span> Active Alerts
                    </h3>
                    <button className="text-[10px] text-blue-400 hover:text-blue-300">
                      Manage settings
                    </button>
                  </div>
                  <p className="text-[10px] text-gray-400 mb-2">Critical issues requiring attention</p>
                  
                  {filteredAlerts.length === 0 ? (
                    <div className="bg-slate-900 rounded-md p-3 text-center">
                      <div className="text-green-400 text-lg mb-1">✓</div>
                      <p className="text-xs text-gray-400">No alerts - everything looks good!</p>
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      {filteredAlerts.slice(0, 3).map((alert, idx) => (
                        <div key={idx} className={`bg-slate-900 rounded-md p-2.5 border-l-4 ${
                          alert.type === 'out_of_stock' ? 'border-red-500' : 
                          alert.type === 'low_stock' ? 'border-yellow-500' : 'border-blue-500'
                        }`}>
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-2">
                              <span className="text-sm">
                                {alert.type === 'out_of_stock' ? '🚨' : alert.type === 'low_stock' ? '⚠️' : '💰'}
                              </span>
                              <div>
                                <h4 className="text-xs font-semibold text-white mb-0.5">
                                  {alert.type === 'out_of_stock' ? 'Out of Stock' : 
                                   alert.type === 'low_stock' ? 'Low Stock' : 'Low Margin'}
                                </h4>
                                <p className="text-[10px] text-gray-400">{alert.sku} - {alert.message}</p>
                              </div>
                            </div>
                            <button className="text-gray-500 hover:text-white">
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))}
                      {filteredAlerts.length > 3 && (
                        <button className="w-full text-center text-[10px] text-blue-400 hover:text-blue-300 py-1">
                          View all {filteredAlerts.length} alerts →
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* AI Recommendations - COMPACT */}
                <div className="bg-slate-800 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xs font-semibold text-white flex items-center gap-1.5">
                      <span>🤖</span> AI Recommendations
                    </h3>
                  </div>
                  <p className="text-[10px] text-gray-400 mb-2">Smart insights to optimize your business</p>
                  
                  {aiRecommendations.length === 0 ? (
                    <div className="bg-slate-900 rounded-md p-3 text-center">
                      <div className="text-gray-400 text-lg mb-1">🤖</div>
                      <p className="text-xs text-gray-400">No recommendations available</p>
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      {aiRecommendations.map((rec, idx) => (
                        <div key={idx} className="bg-slate-900 rounded-md p-2.5">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-2">
                              <span className="text-sm">{rec.icon}</span>
                              <div>
                                <h4 className="text-xs font-semibold text-white mb-0.5">{rec.title}</h4>
                                <p className="text-[10px] text-gray-400">{rec.suggestion}</p>
                                <span className="text-[10px] text-gray-500">{rec.impact}</span>
                              </div>
                            </div>
                            <span className="text-[10px] text-blue-400 font-semibold">{rec.confidence}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'insights' && (
            <div>
              <InsightsDashboard />
            </div>
          )}
          
          {activeTab === 'alerts' && (
            <div className="bg-slate-800 rounded-lg p-4">
              <h2 className="text-sm font-bold text-white mb-3">🚨 Active Alerts</h2>
              {loading ? (
                <p className="text-xs text-gray-400">Loading alerts...</p>
              ) : metrics[4].value === '0' ? (
                <div className="text-center py-6">
                  <div className="text-3xl mb-2">✅</div>
                  <p className="text-sm font-semibold text-white mb-1">All Clear!</p>
                  <p className="text-xs text-gray-400">No critical alerts at this time</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="bg-yellow-500/10 border border-yellow-500/50 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <span className="text-lg">⚠️</span>
                      <div>
                        <h3 className="text-xs font-semibold text-white mb-0.5">Low Stock Warning</h3>
                        <p className="text-[11px] text-gray-400">
                          {metrics[4].value} SKUs are below reorder point
                        </p>
                        <button className="text-[11px] text-blue-400 hover:text-blue-300 mt-1">
                          View Products →
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </HubLayout>
  );
};

export default DashboardHub;
